import React, { useState, useEffect, useCallback, memo } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { getUserInformation, getUserRole } from "../../store/userRole";
import { fetchSubscriptionByUserId } from "../../store/subscriptionSlice";
import moment from "moment";
import "./SubscriptionModal.css";

const SubscriptionModal = ({
  show,
  handleClose,
  classroomId,
  subscription: passedSubscription,
  initialRemainingTime,
}) => {
  const [remainingTime, setRemainingTime] = useState(
    initialRemainingTime ? initialRemainingTime : 0
  ); // Use initialRemainingTime if passed
  const navigate = useNavigate();
  const userInformation = useSelector(getUserInformation);
  const roleOfTheUser = useSelector(getUserRole);

  // Use react-query to fetch subscription data if not passed from the header
  const {
    data: subscription = passedSubscription, // Use passedSubscription if available
    isLoading,
    error,
  } = useQuery(
    ["subscriptionData", classroomId, userInformation?._id],
    () => fetchSubscriptionByUserId(classroomId, userInformation?._id),
    {
      enabled:
        roleOfTheUser === "Student" && !initialRemainingTime && !!classroomId, // Only fetch if subscription wasn't passed
      refetchInterval: 120000, // Refetch every 120 seconds
      staleTime: 30000, // Data remains fresh for 30 seconds
    }
  );

  const calculateRemainingTime = useCallback(() => {
    const currentDate = new Date();
    if (userInformation) {
      const currentClassroom = userInformation.classrooms.find(
        (classroom) => classroom._id === classroomId
      );

      if (currentClassroom) {
        const creationDate = new Date(currentClassroom.createdAt);
        const diffInMilliseconds = currentDate - creationDate;
        const hoursDiff = diffInMilliseconds / (1000 * 60 * 60);

        if (hoursDiff <= 24 && (!subscription || subscription?.error)) {
          const trialEndDate = new Date(
            creationDate.getTime() + 24 * 60 * 60 * 1000
          );
          return getTimeDifference(trialEndDate, currentDate);
        }
      }

      if (subscription && !subscription?.error) {
        const endDate = new Date(subscription.endDate);
        endDate.setHours(23, 59, 59, 999);
        return getTimeDifference(endDate, currentDate);
      }
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, userInformation, classroomId]);

  const getTimeDifference = useCallback((endDate, currentDate) => {
    const diffTime = Math.max(endDate - currentDate, 0);

    if (diffTime === 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      };
    }

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(
      (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffTime % (1000 * 60)) / 1000);

    return {
      days: diffDays,
      hours: diffHours,
      minutes: diffMinutes,
      seconds: diffSeconds,
    };
  }, []);

  // Update the remaining time every second
  useEffect(() => {
    if (show) {
      // If initialRemainingTime is passed, don't calculate again
      if (!remainingTime) {
        setRemainingTime(calculateRemainingTime());
      }

      const timer = setInterval(() => {
        setRemainingTime(calculateRemainingTime());
      }, 1000); // Update every second

      return () => clearInterval(timer); // Clear the interval when the modal is closed
    }
  }, [show, calculateRemainingTime, remainingTime]);

  const handleUpgrade = () => {
    handleClose();
    navigate(`/student/myClassrooms/${classroomId}/pricing`);
  };

  const pluralize = (unit, value) => {
    return value === 1 ? unit.slice(0, -1) : unit;
  };

  const renderRemainingTime = () => {
    // PRIORITY 1: Check if subscription has hourly plan with remaining hours
    if (subscription && subscription?.totalHoursOfStudent !== undefined && subscription?.totalHoursOfStudent !== null) {
      const totalHours = parseFloat(subscription.totalHoursOfStudent) || 0;
      const consumedHours = parseFloat(subscription.hoursConsumedOfStudent) || 0;
      const hoursLeft = totalHours - consumedHours;

      if (hoursLeft <= 0) {
        // Show 0 hours and validity date when hours are exhausted
        const endDate = subscription?.endDate ? new Date(subscription.endDate) : null;
        const now = new Date();

        return (
          <div className="text-center">
            <div className="remaining-time mb-3">
              <div className="d-flex gap-2 align-items-center flex-column">
                <span className="time-number" style={{ fontSize: "3rem" }}>
                  0
                </span>
                <span style={{ fontSize: "14px" }}>
                  Hours Left
                </span>
              </div>
            </div>
            {endDate && endDate > now && (
              <div className="mt-3 p-3" style={{
                backgroundColor: "#fff3cd",
                borderRadius: "8px",
                border: "1px solid #ffc107"
              }}>
                <p className="mb-1" style={{ fontSize: "14px", fontWeight: "500" }}>
                  Portal will be locked on:
                </p>
                <p className="mb-0" style={{ fontSize: "16px", fontWeight: "600", color: "#856404" }}>
                  {moment(endDate).format("MMM D, YYYY [at] h:mm A")}
                </p>
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="remaining-time">
          <div className="d-flex gap-2 align-items-center flex-column">
            <span className="time-number" style={{ fontSize: "3rem" }}>
              {hoursLeft % 1 === 0 ? hoursLeft : hoursLeft.toFixed(1)}
            </span>
            <span style={{ fontSize: "14px" }}>
              {pluralize("Hours", hoursLeft)} Left
            </span>
          </div>
        </div>
      );
    }

    // PRIORITY 2: Fall back to date-based logic
    // Check if there's an error with the specific message "No subscription found"
    if (error?.message?.includes("No subscription found")) {
      return <div className="expired-text">Your trial has expired.</div>;
    }

    if (isLoading && remainingTime === null) {
      return <Spinner animation="border" />;
    }

    if (!remainingTime) {
      return (
        <div className="expired-text">
          Your {subscription && !subscription.error ? "package" : "trial"} has
          expired.
        </div>
      );
    }

    const {
      days = 0,
      hours = 0,
      minutes = 0,
      seconds = 0,
    } = remainingTime || {};

    const isExpired =
      days === 0 && hours === 0 && minutes === 0 && seconds === 0;

    if (isExpired) {
      return (
        <div className="expired-text">
          Your {subscription && !subscription.error ? "package" : "trial"} has
          expired.
        </div>
      );
    }

    return (
      <div className="remaining-time">
        {days > 0 && (
          <div className="d-flex gap-2 align-items-center">
            <span className="time-number">{days}</span>
            <span style={{ fontSize: "10px" }}>{pluralize("Days", days)}</span>
          </div>
        )}
        {hours > 0 && (
          <div className="d-flex gap-2 align-items-center">
            <span className="time-number">{hours}</span>
            <span style={{ fontSize: "10px" }}>
              {pluralize("Hours", hours)}
            </span>
          </div>
        )}
        {minutes > 0 && (
          <div className="d-flex gap-2 align-items-center">
            <span className="time-number">{minutes}</span>
            <span style={{ fontSize: "10px" }}>
              {pluralize("Minutes", minutes)}
            </span>
          </div>
        )}
        {seconds > 0 && (
          <div className="d-flex gap-2 align-items-center">
            <span className="time-number">{seconds}</span>
            <span style={{ fontSize: "10px" }}>
              {pluralize("Seconds", seconds)}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="subscription-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "16px" }}>
          {subscription && subscription?.totalHoursOfStudent !== undefined && subscription?.totalHoursOfStudent !== null
            ? "Your Remaining Hours"
            : subscription && !subscription?.error
            ? "Your Package will Expire in"
            : "Your Trial will Expire in"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center p-5">
        {renderRemainingTime()}
      </Modal.Body>
      <Modal.Footer>
        <Button
          style={{
            backgroundColor: "blue",
            display: "flex",
            justifyContent: "center",
          }}
          onClick={handleUpgrade}
        >
          Upgrade
        </Button>
      </Modal.Footer>
      <style jsx>{`
        .remaining-time {
          display: flex;
          justify-content: center;
          gap: 10px;
          font-size: 1.5rem;
        }
        .time-number {
          font-weight: bold;
        }
        .expired-text {
          font-size: 1rem;
          color: red;
        }
      `}</style>
    </Modal>
  );
};

export default memo(SubscriptionModal);
